import {
  HHAlert,
  HHButton,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useHingeSelectClient } from '../../api';
import { FeeSchedule, ProviderService, ServiceType } from '../../api/types';
import { IsoDateField } from '../components/inputs/iso-date-field';
import { DisabledInfo, StepNav } from '../components/layout/step-nav';
import { SelectableCard } from '../components/selection/selectable-card';
import {
  classifyFeeScheduleInput,
  ContractInput,
  FeeScheduleFlavor,
} from '../context/types';
import { useStepHeader } from '../context/wizard-header.context';
import { useWizardState } from '../context/wizard-state.context';
import { protoDateToIso } from '../utils/iso-to-protodate';
import { getOrgGroups } from '../utils/org-groups';
import { SectionReadiness } from './codes';
import { CaseRateDisplay } from './fee-schedule/case-rate-display';
import { GlobalModalityDisplay } from './fee-schedule/global-modality-display';
import { LineManager } from './line-manager';

const monoText = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 13,
};

const monoCell = {
  ...monoText,
  fontVariantNumeric: 'tabular-nums' as const,
};

type Flavor = FeeScheduleFlavor;

const deriveFsName = (
  clientName: string,
  effectiveDate: string,
  flavor: Flavor,
): string => {
  const ym = effectiveDate.slice(0, 7);
  const label =
    flavor === 'modality'
      ? 'Global Fees'
      : flavor === 'case-rate'
        ? 'Case Rate'
        : 'Fee Schedule';
  return `${clientName} ${label} ${ym}`;
};

interface SectionHeaderProps {
  title: string;
  description?: string;
}

const SectionHeader = ({
  title,
  description,
}: SectionHeaderProps): JSX.Element => (
  <HHStack spacing={0.5} sx={{ pt: 1 }}>
    <HHTypography hhVariant="subtitle1">{title}</HHTypography>
    {description != null && (
      <HHTypography hhVariant="caption" sx={{ opacity: 0.65 }}>
        {description}
      </HHTypography>
    )}
  </HHStack>
);

interface FeeScheduleInnerProps {
  input: ContractInput;
  flavor: Flavor;
}

const FeeScheduleInner = ({
  input,
  flavor,
}: FeeScheduleInnerProps): JSX.Element => {
  const { state, setState } = useWizardState();
  const client = useHingeSelectClient();

  useStepHeader({
    description:
      flavor === 'modality'
        ? 'Compose the service lines that should be priced on this fee schedule. The input modality rows are shown as reference only.'
        : flavor === 'case-rate'
          ? 'Resolve procedure codes, build service lines, and confirm the fee schedule.'
          : 'Compose the service lines from scratch. Bundles and items can be added freely below.',
  });

  const start = input.contract_details.effective_date;
  const end = input.contract_details.term_end;
  const fs = state.decisions.feeSchedule;
  // For multi-TIN inputs, the fee schedule is shared across all contracts.
  // Show the "reuse existing FS" panel whenever *any* TIN is reusing a
  // contract — that contract's attached fee schedules are the operator's
  // primary candidates for reuse. First reusing TIN wins for display.
  const reusingGroup = getOrgGroups(input).find(
    g => state.decisions.contract[g.tax_id]?.mode === 'reuse',
  );
  const reusingContractDecision =
    reusingGroup != null
      ? state.decisions.contract[reusingGroup.tax_id]
      : undefined;
  const reusingContract = reusingContractDecision?.mode === 'reuse';
  const reusedContractId =
    reusingContractDecision?.mode === 'reuse'
      ? reusingContractDecision.existingId
      : null;
  const reusedContractName =
    reusingContractDecision?.mode === 'reuse'
      ? reusingContractDecision.existingName
      : null;

  const fetchedFor = useRef<string | null>(null);
  const [existingSchedules, setExistingSchedules] = useState<FeeSchedule[]>([]);
  const [scheduleLoad, setScheduleLoad] = useState<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  useEffect(() => {
    if (!reusedContractId) return;
    if (fetchedFor.current === reusedContractId) return;
    fetchedFor.current = reusedContractId;
    setScheduleLoad('loading');
    void (async (): Promise<void> => {
      try {
        const result = await client.listFeeSchedules({
          contractId: reusedContractId,
          paginationParams: { pageSize: 50 },
        });
        setExistingSchedules(result.feeSchedules ?? []);
        setScheduleLoad('loaded');
      } catch (e) {
        setScheduleError((e as Error).message);
        setScheduleLoad('error');
      }
    })();
  }, [client, reusedContractId]);

  const reusedFsId = fs?.reuseId ?? null;
  const [reusedLinesStatus, setReusedLinesStatus] = useState<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');
  const [reusedLines, setReusedLines] = useState<ProviderService[]>([]);
  const [reusedLinesError, setReusedLinesError] = useState<string | null>(null);

  useEffect(() => {
    if (reusedFsId == null) {
      setReusedLinesStatus('idle');
      setReusedLines([]);
      return;
    }
    setReusedLinesStatus('loading');
    setReusedLinesError(null);
    void (async (): Promise<void> => {
      try {
        const result = await client.getFeeSchedule({ id: reusedFsId });
        setReusedLines(result.feeSchedule.services ?? []);
        setReusedLinesStatus('loaded');
      } catch (e) {
        setReusedLinesError((e as Error).message);
        setReusedLinesStatus('error');
      }
    })();
  }, [client, reusedFsId]);

  const reuseExistingFs = (sched: FeeSchedule): void => {
    if (sched.id == null) return;
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        feeSchedule: {
          reuseId: sched.id,
          name: sched.name,
          effectiveStart: protoDateToIso(sched.effectiveStart),
          effectiveEnd: protoDateToIso(sched.effectiveEnd),
          lines: [],
        },
      },
    }));
  };

  const switchToCreateNewFs = (): void => {
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        feeSchedule: {
          name: deriveFsName(input.client_name, start, flavor),
          effectiveStart: start,
          effectiveEnd: end,
          lines: [],
        },
      },
    }));
  };

  useEffect(() => {
    if (fs != null) return;
    if (reusingContract) return;
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        feeSchedule: {
          name: deriveFsName(input.client_name, start, flavor),
          effectiveStart: start,
          effectiveEnd: end,
          lines: [],
        },
      },
    }));
  }, [end, fs, input.client_name, reusingContract, setState, start, flavor]);

  const update = (
    field: 'name' | 'effectiveStart' | 'effectiveEnd',
    value: string,
  ): void => {
    setState(s => ({
      ...s,
      decisions: {
        ...s.decisions,
        feeSchedule:
          s.decisions.feeSchedule != null
            ? { ...s.decisions.feeSchedule, [field]: value }
            : s.decisions.feeSchedule,
      },
    }));
  };

  const [codesReady, setCodesReady] = useState<SectionReadiness>({
    ready: false,
  });
  const onCodesReady = useCallback((r: SectionReadiness): void => {
    setCodesReady(r);
  }, []);

  const reusingFs = fs?.reuseId != null;

  const baseReady =
    fs != null &&
    (reusingFs ||
      (fs.name.trim().length > 0 &&
        fs.lines.length > 0 &&
        fs.lines.every(l => l.priceDollars > 0)));

  const canContinue =
    flavor === 'case-rate' ? codesReady.ready && baseReady : baseReady;

  const disabledInfo: DisabledInfo | undefined = (():
    | DisabledInfo
    | undefined => {
    if (canContinue) return undefined;
    if (fs == null) {
      if (reusingContract && scheduleLoad === 'loading') {
        return {
          message: 'Loading fee schedules attached to the existing contract…',
          severity: 'info',
        };
      }
      if (reusingContract) {
        return {
          message:
            'Pick an existing fee schedule on the contract, or choose to create a new one alongside.',
          severity: 'info',
        };
      }
      return { message: 'Initializing fee schedule…', severity: 'info' };
    }
    if (!reusingFs && fs.name.trim().length === 0) {
      return {
        message: 'Enter a fee-schedule name to continue.',
        severity: 'info',
      };
    }
    if (
      flavor === 'case-rate' &&
      !codesReady.ready &&
      codesReady.disabledInfo
    ) {
      return codesReady.disabledInfo;
    }
    if (!reusingFs && fs.lines.length === 0) {
      return {
        message: 'Add at least one service line to continue.',
        severity: 'info',
      };
    }
    if (!reusingFs && fs.lines.some(l => l.priceDollars <= 0)) {
      return {
        message: 'Set a price on every service line to continue.',
        severity: 'warning',
      };
    }
    return undefined;
  })();

  return (
    <HHStack spacing={3}>
      {flavor === 'case-rate' && (
        <CaseRateDisplay input={input} onReady={onCodesReady} />
      )}

      {flavor === 'modality' && <GlobalModalityDisplay input={input} />}

      {flavor === 'unstructured' && (
        <HHAlert hhVariant="outlined" severity="info">
          <HHTypography hhVariant="subtitle2">
            Input not recognized as case-rate or global-by-modality
          </HHTypography>
          <HHTypography hhVariant="body2">
            Starting from scratch — add bundles or items below to build the fee
            schedule manually.
          </HHTypography>
        </HHAlert>
      )}

      <SectionHeader
        title="Fee Schedule"
        description={
          reusingContract
            ? 'Pick an existing fee schedule attached to this contract, or create a new one alongside.'
            : 'Author the new fee schedule that will hold these prices.'
        }
      />

      {reusingContract && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <HHStack spacing={2}>
            <HHStack spacing={0.5}>
              <HHTypography hhVariant="subtitle2">
                Fee schedules already attached to{' '}
                <code>{reusedContractName}</code>
              </HHTypography>
              <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                Reuse one of the existing fee schedules below, or create a new
                one alongside the existing ones.
              </HHTypography>
            </HHStack>

            {scheduleLoad === 'loading' && (
              <HHTypography hhVariant="body2" sx={{ opacity: 0.6 }}>
                Loading…
              </HHTypography>
            )}

            {scheduleLoad === 'error' && (
              <HHAlert hhVariant="outlined" severity="warning">
                Couldn&apos;t fetch attached fee schedules: {scheduleError}.
                Continuing will create a new one.
              </HHAlert>
            )}

            {scheduleLoad === 'loaded' && existingSchedules.length === 0 && (
              <HHTypography hhVariant="body2" sx={{ opacity: 0.6 }}>
                No fee schedules are attached to this contract yet.
              </HHTypography>
            )}

            {scheduleLoad === 'loaded' && existingSchedules.length > 0 && (
              <div
                role="radiogroup"
                aria-label="Existing fee schedules attached to this contract"
              >
                <HHStack spacing={1}>
                  {existingSchedules.map(sched => {
                    const sId = sched.id ?? '';
                    const isPicked = fs?.reuseId === sId;
                    return (
                      <SelectableCard
                        key={sId}
                        selected={isPicked}
                        onClick={(): void => reuseExistingFs(sched)}
                      >
                        <HHTypography hhVariant="body1">
                          {sched.name}
                        </HHTypography>
                        <HHTypography
                          hhVariant="caption"
                          sx={{ ...monoText, opacity: 0.75 }}
                        >
                          {protoDateToIso(sched.effectiveStart)} →{' '}
                          {protoDateToIso(sched.effectiveEnd)}
                        </HHTypography>
                        <HHTypography
                          hhVariant="caption"
                          sx={{ ...monoText, opacity: 0.55 }}
                        >
                          id: {sId}
                        </HHTypography>
                        {isPicked && (
                          <HHTypography
                            hhVariant="caption"
                            sx={{ opacity: 0.55, mt: 0.5 }}
                          >
                            Service lines managed by this schedule (not editable
                            here).
                          </HHTypography>
                        )}
                      </SelectableCard>
                    );
                  })}
                </HHStack>
              </div>
            )}

            <HHStack direction="row" spacing={2}>
              <HHButton
                hhVariant="variant-bypass"
                variant={fs?.reuseId == null ? 'contained' : 'outlined'}
                color="primary"
                onClick={switchToCreateNewFs}
              >
                {fs?.reuseId == null
                  ? 'Create a new fee schedule alongside'
                  : 'Switch to creating a new fee schedule instead'}
              </HHButton>
            </HHStack>
          </HHStack>
        </Paper>
      )}

      {fs != null && fs.reuseId == null && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <HHStack spacing={3}>
            <HHTypography hhVariant="subtitle2">Schedule header</HHTypography>
            <HHTextField
              hhVariant="variant-bypass"
              label="Name"
              helperText="A future contract may attach the same fee schedule — keep this generic."
              value={fs.name}
              onChange={(e): void => update('name', e.target.value)}
              fullWidth
            />
            <HHStack direction="row" spacing={3}>
              <IsoDateField
                label="Effective start"
                value={fs.effectiveStart}
                onChange={(v): void => update('effectiveStart', v ?? '')}
              />
              <IsoDateField
                label="Effective end"
                value={fs.effectiveEnd}
                onChange={(v): void => update('effectiveEnd', v ?? '')}
                minDate={fs.effectiveStart}
              />
            </HHStack>
          </HHStack>
        </Paper>
      )}

      <SectionHeader
        title="Service Lines"
        description={
          reusingFs
            ? 'Lines from the reused fee schedule — read-only. Create a new fee schedule to make changes.'
            : 'Compose the service lines that will be priced on this fee schedule.'
        }
      />

      {reusingFs && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <HHStack spacing={1.5}>
            {reusedLinesStatus === 'loading' && (
              <HHTypography hhVariant="body2" sx={{ opacity: 0.7 }}>
                Loading service lines…
              </HHTypography>
            )}
            {reusedLinesStatus === 'error' && (
              <HHAlert hhVariant="outlined" severity="warning">
                Could not load service lines: {reusedLinesError}
              </HHAlert>
            )}
            {reusedLinesStatus === 'loaded' && reusedLines.length === 0 && (
              <HHTypography hhVariant="body2" sx={{ opacity: 0.6 }}>
                No service lines on this schedule.
              </HHTypography>
            )}
            {reusedLinesStatus === 'loaded' && reusedLines.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 80 }}>Kind</TableCell>
                    <TableCell>Entity ID</TableCell>
                    <TableCell align="right" sx={{ width: 120 }}>
                      Price
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reusedLines.map((svc, idx) => {
                    const kind =
                      svc.kgEntityType === ServiceType.BUNDLE
                        ? 'bundle'
                        : 'item';
                    const amount = svc.prices?.[0]?.amount;
                    const displayPrice =
                      amount != null ? `$${amount.units}` : '—';
                    return (
                      <TableRow
                        key={`${svc.kgEntityType}:${svc.kgEntityId}:${idx}`}
                      >
                        <TableCell sx={monoCell}>{kind}</TableCell>
                        <TableCell sx={monoCell}>{svc.kgEntityId}</TableCell>
                        <TableCell align="right" sx={monoCell}>
                          {displayPrice}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </HHStack>
        </Paper>
      )}

      {!reusingFs && fs != null && <LineManager input={input} />}

      {fs != null && fs.reuseId == null && (
        <HHTypography
          hhVariant="caption"
          sx={{ opacity: 0.55, textAlign: 'right', mt: 1 }}
        >
          State: DRAFT (will transition to EXECUTED in the final commit)
        </HHTypography>
      )}

      <StepNav
        id="fee-schedule"
        canContinue={canContinue}
        disabledInfo={disabledInfo}
      />
    </HHStack>
  );
};

export const FeeScheduleStep = (): JSX.Element => {
  const { state } = useWizardState();
  if (state.input == null) return <Navigate to="../intake" replace />;
  const flavor = classifyFeeScheduleInput(state.input.fee_schedule);
  return <FeeScheduleInner input={state.input} flavor={flavor} />;
};
